import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { m, AnimatePresence } from "framer-motion";
import { Cookie, ShieldCheck, BarChart3, Megaphone, X } from "lucide-react";
import { useLang } from "../i18n";
import { setConsent, readConsent } from "../lib/consent";
import { store } from "../lib/storage";

const KEY = "smarti_cookie_consent";

export default function CookieConsent() {
  const { t } = useLang();
  const [show, setShow] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [ads, setAds] = useState(false);

  useEffect(() => {
    if (!store.get(KEY)) setShow(true);
  }, []);

  useEffect(() => {
    const open = () => {
      const prior = readConsent();
      setAnalytics(prior ? prior.analytics : false);
      setAds(prior ? prior.ads : false);
      setCustomize(true);
      setShow(true);
    };
    window.addEventListener("open-cookie-preferences", open);
    return () => window.removeEventListener("open-cookie-preferences", open);
  }, []);

  const commit = (choice) => {
    setConsent(choice);
    setShow(false);
    setCustomize(false);
  };
  const acceptAll = () => commit({ analytics: true, ads: true });
  const refuseAll = () => commit({ analytics: false, ads: false });
  const saveCustom = () => commit({ analytics, ads });

  return (
    <AnimatePresence>
      {show && !customize && (
        <m.div
          key="banner"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 30 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-x-3 bottom-3 z-[70] mx-auto max-w-2xl rounded-[1.5rem] border border-white/60 bg-white/70 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.18)] backdrop-blur-2xl sm:inset-x-4 sm:bottom-4 sm:p-6"
          data-testid="cookie-consent"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-[#0073FF]/10 text-[#0073FF]">
                <Cookie className="h-5 w-5" />
              </span>
              <p className="text-sm leading-relaxed text-ink-soft">
                {t("Nous utilisons des cookies pour assurer le bon fonctionnement du site et, avec votre accord, mesurer l'audience.")}{" "}
                <Link to="/politique-cookies" className="font-medium text-[#0073FF] hover:underline">{t("En savoir plus")}</Link>.
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2 sm:ml-auto">
              <button
                onClick={refuseAll}
                className="rounded-full border border-black/10 bg-white px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-black/5"
                data-testid="cookie-refuse"
              >
                {t("Refuser")}
              </button>
              <button
                onClick={() => { setCustomize(true); }}
                className="rounded-full border border-black/10 bg-white px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-black/5"
                data-testid="cookie-customize"
              >
                {t("Personnaliser")}
              </button>
              <button
                onClick={acceptAll}
                className="rounded-full bg-[#0073FF] px-5 py-2.5 text-sm font-medium text-white transition-transform hover:-translate-y-0.5"
                data-testid="cookie-accept"
              >
                {t("Tout accepter")}
              </button>
            </div>
          </div>
        </m.div>
      )}

      {show && customize && (
        <m.div
          key="modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[80] grid place-items-center p-4"
          data-testid="cookie-modal"
        >
          <div
            className="absolute inset-0 bg-ink/30 backdrop-blur-sm"
            onClick={() => setCustomize(false)}
          />
          <m.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-lg overflow-hidden rounded-[1.75rem] border border-white/60 bg-white/80 p-6 shadow-[0_30px_80px_rgba(15,23,42,0.25)] backdrop-blur-2xl sm:p-8"
          >
            <button
              onClick={() => setCustomize(false)}
              className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full border border-black/10 bg-white/70 text-ink-soft transition-colors hover:bg-black/5"
              data-testid="cookie-modal-close"
              aria-label={t("Fermer")}
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[#0073FF]/10 text-[#0073FF]">
                <Cookie className="h-5 w-5" />
              </span>
              <div>
                <h2 className="font-display text-xl font-semibold tracking-tight text-ink">{t("Préférences de cookies")}</h2>
                <p className="text-sm text-ink-soft">{t("Choisissez les catégories que vous autorisez.")}</p>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <ConsentRow
                icon={<ShieldCheck className="h-4 w-4" />}
                title={t("Strictement nécessaires")}
                desc={t("Indispensables au fonctionnement du site. Toujours actifs.")}
                checked
                disabled
                testid="cookie-toggle-necessary"
              />
              <ConsentRow
                icon={<BarChart3 className="h-4 w-4" />}
                title={t("Mesure d'audience")}
                desc={t("Statistiques anonymes pour améliorer le site (Analytics).")}
                checked={analytics}
                onChange={setAnalytics}
                testid="cookie-toggle-analytics"
              />
              <ConsentRow
                icon={<Megaphone className="h-4 w-4" />}
                title={t("Marketing")}
                desc={t("Publicités personnalisées et mesure des campagnes.")}
                checked={ads}
                onChange={setAds}
                testid="cookie-toggle-ads"
              />
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-2">
              <button
                onClick={refuseAll}
                className="rounded-full border border-black/10 bg-white px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-black/5"
                data-testid="cookie-modal-refuse"
              >
                {t("Tout refuser")}
              </button>
              <button
                onClick={saveCustom}
                className="rounded-full border border-black/10 bg-white px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-black/5"
                data-testid="cookie-modal-save"
              >
                {t("Enregistrer mes choix")}
              </button>
              <button
                onClick={acceptAll}
                className="ml-auto rounded-full bg-[#0073FF] px-5 py-2.5 text-sm font-medium text-white transition-transform hover:-translate-y-0.5"
                data-testid="cookie-modal-accept"
              >
                {t("Tout accepter")}
              </button>
            </div>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
}

function ConsentRow({ icon, title, desc, checked, onChange, disabled, testid }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-white/60 bg-white/60 p-4 backdrop-blur-xl">
      <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-black/[0.04] text-ink-soft">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">{title}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-ink-soft">{desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange && onChange(!checked)}
        className={`relative mt-1 h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? "bg-[#0073FF]" : "bg-black/15"} ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
        data-testid={testid}
      >
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-[22px]" : "translate-x-0.5"}`} />
      </button>
    </div>
  );
}
